import { tryParse } from "./objUtils";
import dbService from "lib/dexie-db-service";
import * as Sentry from "@sentry/browser";
import moment from "moment";

export interface ICollectionObjectKey {
    document_id: string;
    object_id: string;
    revision: string;
}

export interface ICollectionBrandProperties {
    brand_id: string;
    brand_name: string;
    target_merchant_id: string;
}

const TTL_ALLOCATION_DIMENSION_DATA = 30; // IN DAYS

const readCollectionObject = async (key: ICollectionObjectKey, defVal: any = {}) => {
    try {
        const { value = "" } = await dbService.services.getCollectionObject({
            objectId: key.object_id,
            documentId: key.document_id,
            revision: key.revision,
        });

        return tryParse(value as string, defVal);
    } catch (err) {
        return defVal;
    }
};

const storeCollectionObject = async (key: ICollectionObjectKey, data, createdAt: number) => {
    try {
        dbService.services.saveCollectionObject({
            objectId: key.object_id,
            documentId: key.document_id,
            revision: key.revision,
            createdAt,
            value: JSON.stringify(data),
        });
    } catch (err) {
        Sentry.captureException(err);
    }
};

export const deleteAllocationDimensionsFromStoreIfNeeded = () => {
    try {
        return dbService.services.deleteCollectionObjects(
            moment()
                .add(TTL_ALLOCATION_DIMENSION_DATA * -1, "day")
                .unix()
        );
    } catch (err) {
        Sentry.captureException(err);
    }
};

export const storeAllocationDimensions = async (allocation_dimensions: any[]) => {
    const createdAt = moment().unix();
    allocation_dimensions.forEach((allocationDimension) =>
        storeCollectionObject(
            {
                document_id: allocationDimension.ref.document_id,
                object_id: allocationDimension.ref.object_id,
                revision: allocationDimension.ref.revision,
            },
            allocationDimension,
            createdAt
        )
    );
};

export const readAllocationDimensionsFromStore = async (rollupSnapshot) => {
    const allocationDimensions = [];
    for (const collectionRef of rollupSnapshot.collection_refs) {
        for (const dimension of collectionRef.allocation_dimensions) {
            const allocationDimension = await readCollectionObject(
                {
                    document_id: dimension.document_id,
                    object_id: dimension.object_id,
                    revision: dimension.revision,
                },
                null
            );

            if (!allocationDimension) {
                return { hasDimensions: true, allocationDimensions: null };
            }

            allocationDimensions.push(allocationDimension);
        }
    }

    return {
        hasDimensions: rollupSnapshot.collection_refs.some(
            (collectionRef) => collectionRef.allocation_dimensions.length
        ),
        allocationDimensions,
    };
};
